import React, { lazy, Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
// Eagerly imported because they're on the critical render path of every
// page: App is the auth + chrome shell, and the most common landing
// pages (lookup form + records list + a single person) are what most
// staff hit. Putting these behind Suspense gates the very first paint
// behind a network round-trip for no good reason.
// Side-effect import - sets data-theme on <html> from localStorage
// before the first React paint, avoiding a flash of dark in light mode.
import "./lib/theme";
import { startPerfGovernor } from "./lib/perf";
import { App } from "./App";
import { LookupPage } from "./pages/LookupPage";
import { RecordsPage } from "./pages/RecordsPage";
import { PersonPage } from "./pages/PersonPage";
// Lazy-loaded - these are single-purpose / less-used routes. Pulling
// them out of the main bundle drops the initial JS payload meaningfully
// (each ranges from ~30KB minified to ~150KB+ for stats+visualize that
// import d3, and Leaflet-using country pages). Code is fetched only
// when a user actually navigates there.
const LogPage = lazy(() => import("./pages/LogPage").then((m) => ({ default: m.LogPage })));
const PrintPage = lazy(() => import("./pages/PrintPage").then((m) => ({ default: m.PrintPage })));
const IntakePacketPage = lazy(() =>
  import("./pages/IntakePacketPage").then((m) => ({ default: m.IntakePacketPage })),
);
// (DatesPage is imported eagerly inside TodayPage.tsx now - old /dates
//  route redirects to /today; no lazy import needed here.)
const TodayPage = lazy(() => import("./pages/TodayPage").then((m) => ({ default: m.TodayPage })));
const AdminScrapePage = lazy(() =>
  import("./pages/AdminScrapePage").then((m) => ({ default: m.AdminScrapePage })),
);
const AdminUsersPage = lazy(() =>
  import("./pages/AdminUsersPage").then((m) => ({ default: m.AdminUsersPage })),
);
const StoryPage = lazy(() => import("./pages/StoryPage").then((m) => ({ default: m.StoryPage })));
const StatsPage = lazy(() => import("./pages/StatsPage").then((m) => ({ default: m.StatsPage })));
const DesignLabPage = lazy(() => import("./pages/DesignLabPage").then((m) => ({ default: m.DesignLabPage })));
const LightModeLab = lazy(() => import("./pages/LightModeLab").then((m) => ({ default: m.LightModeLab })));
const SearchVariantsPage = lazy(() =>
  import("./pages/SearchVariantsPage").then((m) => ({ default: m.SearchVariantsPage })),
);
const ClientSummaryPage = lazy(() =>
  import("./pages/ClientSummaryPage").then((m) => ({ default: m.ClientSummaryPage })),
);
const IdeasPage = lazy(() => import("./pages/IdeasPage").then((m) => ({ default: m.IdeasPage })));
const HowToPage = lazy(() => import("./pages/HowToPage").then((m) => ({ default: m.HowToPage })));
const HowToTopicPage = lazy(() =>
  import("./pages/HowToTopicPage").then((m) => ({ default: m.HowToTopicPage })),
);
const QuizPage = lazy(() => import("./pages/QuizPage").then((m) => ({ default: m.QuizPage })));
const LifeQuizPage = lazy(() => import("./pages/LifeQuizPage").then((m) => ({ default: m.LifeQuizPage })));
const InternationalBcPage = lazy(() =>
  import("./pages/InternationalBcPage").then((m) => ({ default: m.InternationalBcPage })),
);
const InternationalBcCountryPage = lazy(() =>
  import("./pages/InternationalBcCountryPage").then((m) => ({
    default: m.InternationalBcCountryPage,
  })),
);
const UsStatesBcPage = lazy(() =>
  import("./pages/UsStatesBcPage").then((m) => ({ default: m.UsStatesBcPage })),
);
const UsStateBcPage = lazy(() =>
  import("./pages/UsStateBcPage").then((m) => ({ default: m.UsStateBcPage })),
);
const BattleMockupPage = lazy(() =>
  import("./pages/BattleMockupPage").then((m) => ({ default: m.BattleMockupPage })),
);
const ResumeMockupPage = lazy(() =>
  import("./pages/ResumeMockupPage").then((m) => ({ default: m.ResumeMockupPage })),
);
const ResumeMockupPage2 = lazy(() =>
  import("./pages/ResumeMockupPage2").then((m) => ({ default: m.ResumeMockupPage2 })),
);
const ResumeRenderPage = lazy(() =>
  import("./pages/ResumeRenderPage").then((m) => ({ default: m.ResumeRenderPage })),
);
const BattlePage = lazy(() =>
  import("./pages/BattlePage").then((m) => ({ default: m.BattlePage })),
);
const AssessmentPage = lazy(() =>
  import("./pages/AssessmentPage").then((m) => ({ default: m.AssessmentPage })),
);
const AutomationRoadmapPage = lazy(() =>
  import("./pages/AutomationRoadmapPage").then((m) => ({ default: m.AutomationRoadmapPage })),
);
// Leaflet's stylesheet has to be loaded before our overrides in
// index.css - otherwise the map tiles render with no positioning
// and the whole map looks blank.
import "leaflet/dist/leaflet.css";
import "./index.css";

/** Lightweight loading fallback shown while a lazy-loaded route's chunk
 *  fetches. Plain text + soft color so it doesn't shift layout. */
function RouteFallback(): JSX.Element {
  return (
    <div
      className="container"
      style={{
        padding: "48px 24px",
        color: "var(--color-text-dim)",
        fontFamily: "var(--font-body)",
        fontSize: 13,
      }}
    >
      Loading…
    </div>
  );
}

/** Scroll the window to the top whenever the route changes. Without this,
 *  React Router preserves scroll position from the previous page - so
 *  navigating from a long topic page lands the user mid-page on the next
 *  one. Hash anchors are still respected (we only scroll if pathname
 *  changes and there's no hash). */
function ScrollToTop(): null {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return; // honor in-page anchors
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname, hash]);
  return null;
}

function scrubVisibleCopy(root: HTMLElement): void {
  const scrub = (text: string) =>
    text
      .replace(/\s*(?:—|--+)\s*/g, ", ")
      .replace(/\s+[–-]\s+/g, ", ")
      .replace(/\s+([,.;:!?])/g, "$1")
      .replace(/,\s*([.!?])/g, "$1")
      .replace(/([a-z0-9)])\.\s+([a-z])/g, "$1, $2")
      .replace(/([,;:])\s+([.!?])/g, "$2")
      .replace(/,\s*,+/g, ",")
      .replace(/[ \t]{2,}/g, " ");
  const skip = (node: Node) => {
    const el = node.parentElement;
    return !!el?.closest("script, style, textarea, input, code, pre, [data-copy-scrub='off']");
  };
  const scrubNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE && !skip(node) && node.nodeValue) {
      const next = scrub(node.nodeValue);
      if (next !== node.nodeValue) node.nodeValue = next;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    let current = walker.nextNode();
    while (current) {
      if (!skip(current) && current.nodeValue) {
        const next = scrub(current.nodeValue);
        if (next !== current.nodeValue) current.nodeValue = next;
      }
      current = walker.nextNode();
    }
  };
  scrubNode(root);
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(scrubNode);
      if (mutation.type === "characterData") scrubNode(mutation.target);
    }
  });
  observer.observe(root, { childList: true, characterData: true, subtree: true });
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root not found");

startPerfGovernor();
scrubVisibleCopy(rootEl);

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <BrowserRouter>
      <ScrollToTop />
      {/* Suspense covers the entire route tree so any lazy-loaded page
          gets a fallback while its chunk fetches. Eager routes (App
          shell, LookupPage, RecordsPage, PersonPage) render synchronously
          and never trigger the fallback. */}
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Bare-chrome print routes - outside <App> so they have no
              header/nav. Both open in a new tab and auto-fire window.print()
              when ?print=1 is passed. */}
          <Route path="/print/:id" element={<PrintPage />} />
          <Route path="/intake/:id" element={<IntakePacketPage />} />
          {/* ASSESSMENT MEETING - live-transcribed sit-down with a
              participant. Drives a configurable checklist (legal intake,
              employment, general) via Deepgram + gpt-4.1-nano and writes
              extracted facts back into the profile + notes on save.
              Bare-chrome so the recording UI owns the viewport. */}
          <Route path="/assessment/:id" element={<AssessmentPage />} />
          {/* PUBLIC LEGAL-TEAM AUTOMATION ROADMAP - no login required.
              Standalone shareable page intended for NJRC leadership /
              partner-org review. Bare chrome so the page is its own
              experience. Static content; safe to share with anyone. */}
          <Route path="/njrc-automation" element={<AutomationRoadmapPage />} />
          {/* Client-takeaway summary - bare-chrome, NJRC-branded, also
              doubles as the source-of-truth render for the server-side
              PDF endpoint (/api/jobs/:id/client-summary.pdf). */}
          <Route path="/client-summary/:id" element={<ClientSummaryPage />} />
          {/* LSC certification quiz - full-screen takeover, no app shell.
              Lives outside <App> so the quiz owns the entire viewport. */}
          <Route path="/how-to/quiz" element={<QuizPage />} />
          {/* Life Quiz - participant-facing companion to the LSC certification
              quiz. Same visual identity, different question pool. Lives outside
              the App shell so the whole viewport is ours, like the LSC quiz. */}
          <Route path="/life-quiz" element={<LifeQuizPage />} />
          {/* record list ARENA - trading-card-game mockup for Damon to pick a
              visual style. Lives outside <App> so the mockup can fill the
              entire viewport without our app chrome competing for attention. */}
          <Route path="/labs/battle" element={<BattleMockupPage />} />
          {/* RESUME STUDIO - six designer-grade resume mockups using a
              shared anonymized participant. Bare-chrome so the sheets get
              the full viewport; designed to print as a 6-page packet. */}
          <Route path="/labs/resumes" element={<ResumeMockupPage />} />
          <Route path="/labs/resumes/2" element={<ResumeMockupPage2 />} />
          {/* Bare-chrome resume render page used by the headless PDF
              renderer. Reads ?token=…&pdf=1 from the URL, fetches the
              stashed draft, and signals data-pdf-ready when fonts are
              settled so Playwright captures cleanly. */}
          <Route path="/resume/render" element={<ResumeRenderPage />} />
          {/* record list ARENA - the actual playable game, real intake data
              feeds the roster. Lives outside <App> so the arena fully
              takes over the viewport (cyber-mugshot black + neon, no app
              chrome competing for attention). */}
          <Route path="/battle" element={<BattlePage />} />
          <Route element={<App />}>
            {/* / now lands on /today (the new home page) instead of /lookup.
                Lookup is still primary nav but is purely the "add a person"
                form now - Today is where staff actually start their day. */}
            <Route index element={<Navigate to="/today" replace />} />
            <Route path="/lookup" element={<LookupPage />} />
            <Route path="/log" element={<LogPage />} />
            <Route path="/records" element={<RecordsPage />} />
            <Route path="/records/:id" element={<PersonPage />} />
            {/* /dates content is now rendered at the bottom of /today.
                Old bookmarks redirect to keep the page in working
                order; the in-app primary nav no longer surfaces /dates. */}
            <Route path="/dates" element={<Navigate to="/today" replace />} />
            <Route path="/today" element={<TodayPage />} />
            <Route path="/story" element={<StoryPage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/design-lab" element={<DesignLabPage />} />
            <Route path="/design-lab/light" element={<LightModeLab />} />
            <Route path="/admin/scrape-health" element={<AdminScrapePage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/ideas" element={<IdeasPage />} />
            {/* HOW TO knowledge base. International birth-cert routes need
                to come BEFORE the generic /how-to/:slug catcher so the
                picker + country pages don't get swallowed. */}
            <Route path="/how-to" element={<HowToPage />} />
            <Route
              path="/how-to/international-birth-certificates"
              element={<InternationalBcPage />}
            />
            <Route
              path="/how-to/international-birth-certificates/:code"
              element={<InternationalBcCountryPage />}
            />
            {/* US states birth-cert picker + per-state detail pages, same
                precedence trick as international: pickers come first so
                they're not swallowed by /how-to/:slug. */}
            <Route path="/how-to/us-states-birth-certificates" element={<UsStatesBcPage />} />
            <Route path="/how-to/us-states-birth-certificates/:code" element={<UsStateBcPage />} />
            <Route path="/how-to/:slug" element={<HowToTopicPage />} />
            <Route path="/search-variants" element={<SearchVariantsPage />} />
            <Route path="*" element={<Navigate to="/today" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  </React.StrictMode>,
);
